<template>
  <div class="privacy-page">
    <!-- Header -->
    <header class="page-header">
      <div class="header-inner">
        <p class="brand">Hayok Insurance</p>
        <h1>Privacy <em>Policy</em></h1>
        <p class="meta">Effective immediately upon posting</p>
      </div>
    </header>

    <!-- Layout -->
    <div class="layout">
      <!-- Sidebar TOC -->
      <aside class="sidebar">
        <p class="toc-label">Contents</p>
        <a
          v-for="section in sections"
          :key="section.id"
          :href="`#${section.id}`"
          class="toc-link"
          :class="{ active: activeSection === section.id }"
        >
          {{ section.title }}
        </a>
      </aside>

      <!-- Main Content -->
      <main class="content">
        <div class="intro-box">
          This page informs visitors of our policies regarding the collection,
          use, and disclosure of Personal Information when you use
          <strong>hayokinsurance.com</strong>. By using our Service, you agree
          to the collection and use of information in accordance with this
          policy.
        </div>

        <div
          v-for="(section, index) in sections"
          :key="section.id"
          :id="section.id"
          class="section-card"
        >
          <div class="section-header">
            <span class="section-number">{{
              String(index + 1).padStart(2, "0")
            }}</span>
            <h2 class="section-title">{{ section.title }}</h2>
          </div>
          <div class="section-body" v-html="section.content" />
        </div>
      </main>
    </div>

    <!-- Footer -->
    <footer class="page-footer">
      &copy; Hayok Insurance &nbsp;·&nbsp; hayokinsurance.com &nbsp;·&nbsp;
      Questions? <a href="mailto:contact@hayokinsurance.com">Contact us</a>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const activeSection = ref("");

const sections = [
  {
    id: "information-collection",
    title: "Information Collection & Use",
    content: `For a better experience while using our Service, we may require you to provide us with
      certain personally identifiable information, including but not limited to your
      <strong>name, phone number, and postal address</strong>. The information that we collect
      will be used to contact or identify you.`,
  },
  {
    id: "log-data",
    title: "Log Data",
    content: `Whenever you visit our Service, we collect information that your browser sends to us
      called Log Data. This may include:
      <ul>
        <li>Your computer's Internet Protocol (IP) address</li>
        <li>Browser version and type</li>
        <li>Pages of our Service that you visit</li>
        <li>Time and date of your visit</li>
        <li>Time spent on those pages and other statistics</li>
      </ul>`,
  },
  {
    id: "cookies",
    title: "Cookies",
    content: `Cookies are files with a small amount of data, commonly used as an anonymous unique
      identifier. Our website uses cookies to collect information and improve our Service.
      You may accept or refuse cookies at any time — note that refusing cookies may limit
      certain features of our Service.`,
  },
  {
    id: "data-retention",
    title: "Data Retention",
    content: `We retain your personal data only for as long as is necessary to fulfil the purposes
      for which it was collected, or as required by law. Specifically:
      <ul>
        <li><strong>Account information</strong> is retained for the duration of your active account and deleted within <strong>90 days</strong> of account deletion.</li>
        <li><strong>Insurance policy and claims data</strong> is retained for a minimum of <strong>7 years</strong> in accordance with applicable financial and insurance regulations.</li>
        <li><strong>Log data and usage statistics</strong> are retained for up to <strong>12 months</strong> before being anonymised or deleted.</li>
        <li><strong>Communication records</strong> (emails, support messages) are retained for up to <strong>3 years</strong> for quality assurance and dispute resolution purposes.</li>
      </ul>
      After the applicable retention period, your data is securely deleted or anonymised so that it
      can no longer be associated with you. You may request early deletion of your data at any time
      by contacting us at <strong>support@hayokmedicare.ng</strong>, subject to any legal obligations
      that require us to retain it.`,
  },
  {
    id: "service-providers",
    title: "Service Providers",
    content: `We may employ third-party companies and individuals for the following reasons:
      <ul>
        <li>To facilitate our Service</li>
        <li>To provide the Service on our behalf</li>
        <li>To perform Service-related services</li>
        <li>To assist us in analyzing how our Service is used</li>
      </ul>
      These third parties have access to your Personal Information solely to perform tasks assigned
      to them on our behalf and are obligated not to disclose or use it for any other purpose.`,
  },
  {
    id: "security",
    title: "Security",
    content: `We value your trust in providing us your Personal Information and strive to use
      commercially acceptable means of protecting it. However, no method of transmission over the
      internet or electronic storage is 100% secure, and we cannot guarantee its absolute security.`,
  },
  {
    id: "links",
    title: "Links to Other Sites",
    content: `Our Service may contain links to other sites. If you click on a third-party link,
      you will be directed to that site. These external sites are <strong>not operated by us</strong>.
      We strongly advise you to review the Privacy Policy of any website you visit. We have no
      control over, and assume no responsibility for, the content or privacy practices of any
      third-party sites or services.`,
  },
  {
    id: "children",
    title: "Children's Privacy",
    content: `Our Services do not address anyone under the age of <strong>13</strong>. We do not
      knowingly collect personally identifiable information from children under 13. If we discover
      that a child under 13 has provided personal information, we immediately delete it. If you are
      a parent or guardian and believe your child has provided us with personal information,
      please contact us so we can take the necessary action.`,
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    content: `We may update our Privacy Policy from time to time. We advise you to review this page
      periodically for any changes. Changes are effective <strong>immediately</strong> after they
      are posted on this page.`,
  },
  {
    id: "contact",
    title: "Contact Us",
    content: `If you have any questions or suggestions about our Privacy Policy, do not hesitate
      to reach out to us at <strong>support@hayokmedicare.ng</strong>.`,
  },
];

let observer;

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          activeSection.value = entry.target.id;
        }
      });
    },
    { rootMargin: "-30% 0px -60% 0px" }
  );

  sections.forEach((s) => {
    const el = document.getElementById(s.id);
    if (el) observer.observe(el);
  });
});

onUnmounted(() => {
  if (observer) observer.disconnect();
});
</script>

<style scoped>
.privacy-page {
  min-height: 100vh;
  background: #f5f2ec;
  color: #202948;
  font-family: "DM Sans", sans-serif;
  font-weight: 300;
}

/* Header */
.page-header {
  background: #202948;
  color: #f5f2ec;
  padding: 2.5rem 2rem 2.2rem;
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: "";
  position: absolute;
  right: -80px;
  top: -80px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  border: 60px solid #ffb562;
  opacity: 0.15;
  pointer-events: none;
}

.header-inner {
  max-width: 860px;
  margin: 0 auto;
  position: relative;
}

.brand {
  font-size: 0.72rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #ffb562;
  margin-bottom: 0.9rem;
  font-weight: 500;
}

.header-inner h1 {
  font-family: "DM Serif Display", serif;
  font-size: clamp(2rem, 5vw, 3.4rem);
  font-weight: 400;
  line-height: 1.1;
}

.header-inner h1 em {
  color: #ffb562;
  font-style: italic;
}

.meta {
  margin-top: 1.2rem;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: rgba(245, 242, 236, 0.4);
  text-transform: uppercase;
}

/* Layout */
.layout {
  display: grid;
  grid-template-columns: 220px 1fr;
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 1.5rem 5rem;
  align-items: start;
}

@media (max-width: 700px) {
  .layout {
    grid-template-columns: 1fr;
  }
  .sidebar {
    display: none;
  }
}

/* Sidebar */
.sidebar {
  position: sticky;
  top: 2rem;
  padding: 2.5rem 1.5rem 2.5rem 0;
}

.toc-label {
  font-size: 0.68rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #7a6f65;
  margin-bottom: 1rem;
}

.toc-link {
  display: block;
  font-size: 0.82rem;
  color: #7a6f65;
  text-decoration: none;
  padding: 0.4rem 0 0.4rem 0.9rem;
  border-left: 2px solid #d9d4cb;
  transition: color 0.2s, border-color 0.2s;
  line-height: 1.35;
}

.toc-link:hover,
.toc-link.active {
  color: #ffb562;
  border-color: #ffb562;
}

/* Content */
.content {
  padding: 3rem 0 0 2.5rem;
  border-left: 1px solid #d9d4cb;
}

@media (max-width: 700px) {
  .content {
    padding: 2rem 0 0;
    border-left: none;
  }
}

.intro-box {
  background: #202948;
  color: #f5f2ec;
  border-radius: 4px;
  padding: 1.6rem 2rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  line-height: 1.75;
}

.intro-box strong {
  color: #ffb562;
}

/* Section Card */
.section-card {
  background: #ffffff;
  border: 1px solid #d9d4cb;
  border-radius: 4px;
  padding: 2rem;
  margin-bottom: 1.2rem;
  scroll-margin-top: 5rem;
  transition: box-shadow 0.25s;
}

.section-card:hover {
  box-shadow: 0 4px 24px rgba(32, 41, 72, 0.07);
}

.section-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.section-number {
  flex-shrink: 0;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #ffb562;
  background: rgba(255, 181, 98, 0.12);
  border: 1px solid rgba(255, 181, 98, 0.35);
  border-radius: 2px;
  padding: 0.25rem 0.55rem;
  margin-top: 0.2rem;
}

.section-title {
  font-family: "DM Serif Display", serif;
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 1.3;
}

.section-body {
  font-size: 0.9rem;
  line-height: 1.8;
  color: #2e2a26;
}

.section-body :deep(ul) {
  margin: 0.8rem 0;
  padding: 0;
  list-style: none;
}

.section-body :deep(li) {
  padding-left: 1.2rem;
  position: relative;
  margin-bottom: 0.35rem;
}

.section-body :deep(li)::before {
  content: "—";
  position: absolute;
  left: 0;
  color: #ffb562;
}

.section-body :deep(strong) {
  font-weight: 500;
  color: #202948;
}

/* Footer */
.page-footer {
  background: #202948;
  color: rgba(245, 242, 236, 0.45);
  text-align: center;
  padding: 2rem;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
}

.page-footer a {
  color: #ffb562;
  text-decoration: none;
}
.page-footer a:hover {
  text-decoration: underline;
}
</style>
